import React from 'react';
import { CommonUtils, ProjectDetail } from './commonUtils';

// 공통 타입 정의
export interface FormData {
  brandCode?: string;
  outletCode: string;
  status: string;
  number: number;
  type: string;
  name: string;
  shortName: string;
  salesTime: string;
  comment: string;
  intro: string;
  phoneNumber: string;
  ownerPhoneNumber: string;
  homepage: string;
  postalCode: string;
  regionCode: string;
  addressRoad: string;
  addressDetail: string;
  lat: number;
  lon: number;
  loginPassword: string;
}

export interface FormErrors {
  brandCode?: string;
  outletCode?: string;
  name?: string;
  number?: string;
  phoneNumber?: string;
  addressRoad?: string;
  addressDetail?: string;
  location?: string;
  loginPassword?: string;
}

export interface AddressData {
  postalCode: string;
  road: string;
  regionCode: string;
  lat: number;
  lon: number;
}

// 공통 유틸리티 함수들
export class ShopFormUtils {
  // 폼 검증 함수 (기본)
  static validateForm(formData: FormData): FormErrors {
    return this.validateCreateForm(formData);
  }

  // 매장 생성 폼 검증
  static validateCreateForm(formData: FormData): FormErrors {
    const newErrors: FormErrors = {};
    
    // Required fields
    if (!formData.outletCode) newErrors.outletCode = '매장 코드는 필수입니다';
    if (!formData.name) newErrors.name = '매장명은 필수입니다';
    if (!formData.number) newErrors.number = '매장 번호는 필수입니다';
    if (!formData.phoneNumber) newErrors.phoneNumber = '매장 전화번호는 필수입니다';
    if (!formData.addressRoad) newErrors.addressRoad = '도로명 주소는 필수입니다';
    if (!formData.addressDetail) newErrors.addressDetail = '상세 주소는 필수입니다';
    if (!formData.lat || !formData.lon) newErrors.location = '위치 좌표는 필수입니다';
    if (!formData.loginPassword) newErrors.loginPassword = '로그인 비밀번호는 필수입니다';
    
    // Format validations
    if (formData.phoneNumber && !/^[0-9-]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = '올바른 전화번호 형식이 아닙니다';
    }
    
    return newErrors;
  }

  // 매장 수정 폼 검증
  static validateUpdateForm(formData: FormData): FormErrors {
    const newErrors: FormErrors = {};
    
    // Required fields (수정 시에는 일부 필드가 선택적일 수 있음)
    if (!formData.outletCode) newErrors.outletCode = '매장 코드는 필수입니다';
    if (!formData.name) newErrors.name = '매장명은 필수입니다';
    if (!formData.number) newErrors.number = '매장 번호는 필수입니다';
    if (!formData.phoneNumber) newErrors.phoneNumber = '매장 전화번호는 필수입니다';
    if (!formData.addressRoad) newErrors.addressRoad = '도로명 주소는 필수입니다';
    if (!formData.addressDetail) newErrors.addressDetail = '상세 주소는 필수입니다';
    if (!formData.lat || !formData.lon) newErrors.location = '위치 좌표는 필수입니다';
    // 수정 시에는 로그인 비밀번호가 선택적일 수 있음 (기존 비밀번호 유지)
    
    // Format validations
    if (formData.phoneNumber && !/^[0-9-]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = '올바른 전화번호 형식이 아닙니다';
    }
    
    return newErrors;
  }

  // 입력값 변경 처리 함수
  static handleInputChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    setFormData: React.Dispatch<React.SetStateAction<FormData>>,
    setErrors: React.Dispatch<React.SetStateAction<FormErrors>>
  ) {
    const { name, value } = e.target;
    
    // 전화번호 필드는 숫자만 허용
    if (name === 'phoneNumber' || name === 'ownerPhoneNumber') {
      const numericValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'number' ? parseInt(value) || 0 : 
                name === 'lat' || name === 'lon' ? parseFloat(value) || 0 : value
      }));
    }
    
    // Clear error when user types
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  }

  // 매장 타입 배지 생성
  static getTypeBadge(type: string) {
    return CommonUtils.getTypeBadge(type);
  }

  // 매장 상태 배지 생성
  static getStatusBadge(status: string) {
    return CommonUtils.getStatusBadge(status, 'shop');
  }

  // 날짜 포맷팅
  static formatDate(dateString: string): string {
    return CommonUtils.formatDate(dateString);
  }

  // 가격 포맷팅
  static formatPrice(price: number): string {
    return CommonUtils.formatPrice(price);
  }

  // 프로젝트 정보 로드
  static loadProjectDetail(): any {
    return CommonUtils.loadProjectDetail();
  }

  // 기본 폼 데이터
  static getDefaultFormData(): FormData {
    return {
      brandCode: '',
      outletCode: '',
      status: 'PENDING',
      number: 0,
      type: 'franchise',
      name: '',
      shortName: '',
      salesTime: '',
      comment: '',
      intro: '',
      phoneNumber: '',
      ownerPhoneNumber: '',
      homepage: '',
      postalCode: '',
      regionCode: '',
      addressRoad: '',
      addressDetail: '',
      lat: 0,
      lon: 0,
      loginPassword: ''
    };
  }

  // 모바일 사이드바 리사이즈 핸들러
  static useMobileSidebarResize(
    setIsMobileSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>
  ) {
    return CommonUtils.useMobileSidebarResize(setIsMobileSidebarOpen);
  }

  // 체크박스 관련 함수들
  static handleSelectAll(
    selectedShops: string[],
    filteredShops: any[],
    setSelectedShops: React.Dispatch<React.SetStateAction<string[]>>
  ) {
    return CommonUtils.handleSelectAll(selectedShops, filteredShops, setSelectedShops, (shop) => shop.code);
  }

  static handleSelectShop(
    shopCode: string,
    selectedShops: string[],
    setSelectedShops: React.Dispatch<React.SetStateAction<string[]>>
  ) {
    return CommonUtils.handleSelectItem(shopCode, selectedShops, setSelectedShops);
  }

  // 페이지네이션 관련 함수들
  static handlePageChange(
    page: number,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  ) {
    return CommonUtils.handlePageChange(page, setCurrentPage);
  }

  static handlePageSizeChange(
    size: number,
    setPageSize: React.Dispatch<React.SetStateAction<number>>,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  ) {
    return CommonUtils.handlePageSizeChange(size, setPageSize, setCurrentPage);
  }

  // 정렬 관련 함수들
  static handleSort(
    field: string,
    sortField: string,
    sortOrder: string,
    setSortField: React.Dispatch<React.SetStateAction<string>>,
    setSortOrder: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleSort(field, sortField, sortOrder, setSortField, setSortOrder);
  }

  // 검색 및 필터 관련 함수들
  static handleSearch(
    term: string,
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleSearch(term, setSearchTerm);
  }

  static handleStatusFilter(
    status: string,
    setSelectedStatus: React.Dispatch<React.SetStateAction<string>>
  ) {
    return CommonUtils.handleStatusFilter(status, setSelectedStatus);
  }

  // 뷰 타입 변경 핸들러
  static handleViewTypeChange(
    newViewType: string,
    setViewType: React.Dispatch<React.SetStateAction<string>>,
    location: any,
    navigate: any
  ) {
    return CommonUtils.handleViewTypeChange(newViewType, setViewType, location, navigate, '/shops');
  }

  // 매장 생성 페이지로 이동
  static handleShopCreate(navigate: (path: string) => void) {
    navigate('/shops/create');
  }

  // 매장 수정 페이지로 이동
  static handleShopEdit(navigate: (path: string) => void, shopCode: string, currentPage: number, pageSize: number) {
    navigate(`/shops/${encodeURIComponent(shopCode)}/update?page=${currentPage}&size=${pageSize}`);
  }

  // TODO: 사용 여부 고민해볼 것.
  static handleShopView(navigate: (path: string) => void, shopCode: string) {
    navigate(`/shops/view/${shopCode}`);
  }

  static handleShopDelete(shopCode: string, onDelete?: (code: string) => void) {
    return CommonUtils.handleDelete(shopCode, '매장', onDelete);
  }

  // 폼 제출 처리 (매장 생성)
  static async handleCreateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    getProjectCode: () => string,
    createShop: (projectCode: string, data: any) => Promise<any>,
    navigate: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const projectCode = getProjectCode();
      const shopData = {
        outletCode: formData.outletCode,
        status: formData.status,
        number: formData.number,
        type: formData.type,
        name: formData.name,
        brandCode: formData.brandCode || undefined,
        shortName: formData.shortName || undefined,
        salesTime: formData.salesTime || undefined,
        comment: formData.comment || undefined,
        intro: formData.intro || undefined,
        phoneNumber: formData.phoneNumber,
        ownerPhoneNumber: formData.ownerPhoneNumber || undefined,
        homepage: formData.homepage || undefined,
        postalCode: formData.postalCode || undefined,
        regionCode: formData.regionCode || undefined,
        addressRoad: formData.addressRoad,
        addressDetail: formData.addressDetail,
        lat: formData.lat,
        lon: formData.lon,
        loginPassword: formData.loginPassword || undefined
      };

      const result = await createShop(projectCode, shopData);
      
      if (result.success) {
        alert('매장이 성공적으로 생성되었습니다.');
        navigate('/shops');
      } else {
        setSubmitError(result.message || '매장 생성에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('매장 생성 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '매장 생성 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  // 폼 제출 처리 (매장 수정)
  static async handleUpdateSubmit(
    e: React.FormEvent,
    formData: FormData,
    validateForm: () => boolean,
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
    setSubmitError: React.Dispatch<React.SetStateAction<string | null>>,
    setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
    setOriginalData: React.Dispatch<React.SetStateAction<FormData | null>>,
    getProjectCode: () => string,
    shopCode: string,
    updateShop: (projectCode: string, shopCode: string, data: any) => Promise<any>,
    navigate: any,
    location: any
  ) {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const projectCode = getProjectCode();
      
      if (!shopCode) {
        setSubmitError('매장 코드가 없습니다.');
        return;
      }

      // API 요청 데이터 준비
      const updateData = {
        outletCode: formData.outletCode,
        status: formData.status,
        number: formData.number,
        type: formData.type,
        name: formData.name,
        brandCode: formData.brandCode || undefined,
        shortName: formData.shortName || undefined,
        salesTime: formData.salesTime || undefined,
        comment: formData.comment || undefined,
        intro: formData.intro || undefined,
        phoneNumber: formData.phoneNumber,
        ownerPhoneNumber: formData.ownerPhoneNumber || undefined,
        homepage: formData.homepage || undefined,
        postalCode: formData.postalCode || undefined,
        regionCode: formData.regionCode || undefined,
        addressRoad: formData.addressRoad,
        addressDetail: formData.addressDetail,
        lat: formData.lat,
        lon: formData.lon,
        loginPassword: formData.loginPassword || undefined
      };

      const result = await updateShop(projectCode, shopCode, updateData);

      if (result.success) {
        alert('매장 정보가 성공적으로 수정되었습니다.');
        setHasChanges(false);
        setOriginalData({...formData});
        // 매장 목록으로 이동 (페이징 정보 유지)
        const searchParams = new URLSearchParams(location.search);
        const page = searchParams.get('page') || '0';
        const size = searchParams.get('size') || '20';
        navigate(`/shops?page=${page}&size=${size}`);
      } else {
        setSubmitError(result.message || '매장 수정에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('매장 수정 오류:', error);
      // 서버에서 보낸 에러 메시지 우선 사용
      const displayMessage = error?.message || CommonUtils.getNetworkErrorMessage(error) || '매장 수정 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  }
}